import { useState } from 'react';
import { Check, Copy } from 'lucide-react';

import { Button } from '~shared/shadcn/ui/button';
import { copyToClipboard } from '~shared/utils/clipboard';
import { EMBED_RESIZE_MESSAGE_TYPE } from '../lib/embed-resize';

interface EmbedSnippetProps {
  studioId: string;
  formType: string;
  formId?: string;
  formName: string;
}

export function buildEmbedUrl(
  studioId: string,
  formType: string,
  formId?: string,
): string {
  const params = new URLSearchParams({ studioId, formType });
  if (formId) params.set('formId', formId);
  return `${window.location.origin}/embed/form?${params.toString()}`;
}

export function EmbedSnippet({
  studioId,
  formType,
  formId,
  formName,
}: EmbedSnippetProps) {
  const [copied, setCopied] = useState(false);
  const embedUrl = buildEmbedUrl(studioId, formType, formId);
  // The iframe posts its content height to the parent; the inline listener
  // resizes the frame so the card never scrolls or clips (ENR-1474). Native
  // iframe scrolling stays enabled as the fallback when scripts cannot run.
  const snippet = `<iframe src="${embedUrl}" style="width:100%;max-width:480px;border:0;" height="640" title="${formName}" loading="lazy"></iframe>
<script>(function(){var f=document.currentScript.previousElementSibling;window.addEventListener('message',function(e){if(e.source!==f.contentWindow)return;var d=e.data||{};if(d.type==='${EMBED_RESIZE_MESSAGE_TYPE}'&&typeof d.height==='number'){f.style.height=d.height+'px';}});})();</script>`;

  const handleCopy = () => {
    void copyToClipboard(snippet).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 2000);
    });
  };

  return (
    <div className='space-y-2'>
      <p className='text-xs text-muted-foreground'>
        Paste this snippet into your website where the form should appear. It
        sizes itself to the form automatically.
      </p>
      <div className='relative rounded-md bg-muted p-3'>
        <pre className='whitespace-pre-wrap break-all pr-10 font-mono text-xs'>
          {snippet}
        </pre>
        <Button
          size='icon'
          variant='ghost'
          className='absolute right-1 top-1 size-7'
          onClick={handleCopy}
          aria-label='Copy embed snippet'
        >
          {copied ? (
            <Check className='size-3.5 text-green-600' />
          ) : (
            <Copy className='size-3.5' />
          )}
        </Button>
      </div>
      <p className='text-xs text-muted-foreground'>
        Direct link:{' '}
        <a
          href={embedUrl}
          target='_blank'
          rel='noreferrer'
          className='break-all underline'
        >
          {embedUrl}
        </a>
      </p>
    </div>
  );
}
